export const PACKAGING_ITEMS_LAST_UPDATED_WINDOWS = [
  '30m',
  '1h',
  '3h',
  '6h',
  '12h',
  '24h',
  '2d',
  '3d',
  '7d',
] as const

export type PackagingItemsLastUpdatedWindow =
  (typeof PACKAGING_ITEMS_LAST_UPDATED_WINDOWS)[number]

export const PACKAGING_ITEMS_LAST_UPDATED_SCOPES = [
  'supplier',
  'other',
] as const

export type PackagingItemsLastUpdatedScope =
  (typeof PACKAGING_ITEMS_LAST_UPDATED_SCOPES)[number]

export type PackagingItemsLastUpdatedFilter =
  `${PackagingItemsLastUpdatedScope}:${PackagingItemsLastUpdatedWindow}`

export const PACKAGING_ITEMS_LAST_UPDATED_FILTERS =
  PACKAGING_ITEMS_LAST_UPDATED_SCOPES.flatMap((scope) =>
    PACKAGING_ITEMS_LAST_UPDATED_WINDOWS.map(
      (window) => `${scope}:${window}` as PackagingItemsLastUpdatedFilter
    )
  )

const PACKAGING_ITEMS_LAST_UPDATED_FILTER_MS: Record<
  PackagingItemsLastUpdatedWindow,
  number
> = {
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '3h': 3 * 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '12h': 12 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '2d': 2 * 24 * 60 * 60 * 1000,
  '3d': 3 * 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
}

function parseLastUpdatedFilterParts(
  value: unknown
): { scope: PackagingItemsLastUpdatedScope; window: PackagingItemsLastUpdatedWindow } | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const separatorIndex = value.indexOf(':')
  if (separatorIndex <= 0) {
    return undefined
  }
  const scope = value.slice(0, separatorIndex).trim()
  const window = value.slice(separatorIndex + 1).trim()
  if (
    !PACKAGING_ITEMS_LAST_UPDATED_SCOPES.includes(
      scope as PackagingItemsLastUpdatedScope
    )
  ) {
    return undefined
  }
  if (
    !PACKAGING_ITEMS_LAST_UPDATED_WINDOWS.includes(
      window as PackagingItemsLastUpdatedWindow
    )
  ) {
    return undefined
  }
  return {
    scope: scope as PackagingItemsLastUpdatedScope,
    window: window as PackagingItemsLastUpdatedWindow,
  }
}

export function parsePackagingItemsLastUpdatedFilter(
  value: unknown
): PackagingItemsLastUpdatedFilter | undefined {
  const parts = parseLastUpdatedFilterParts(value)
  if (parts == null) {
    return undefined
  }
  return `${parts.scope}:${parts.window}`
}

export function itemMatchesLastUpdatedFilter(
  row: {
    lastChangeHistoryAtBySupplier: string | null | undefined
    lastChangeHistoryAtByOther: string | null | undefined
  },
  filter: PackagingItemsLastUpdatedFilter,
  now: Date = new Date()
): boolean {
  const parts = parseLastUpdatedFilterParts(filter)
  if (parts == null) {
    return false
  }
  const lastChangeHistoryAt =
    parts.scope === 'supplier'
      ? row.lastChangeHistoryAtBySupplier
      : row.lastChangeHistoryAtByOther
  if (lastChangeHistoryAt == null || lastChangeHistoryAt.trim() === '') {
    return false
  }
  const timestampMs = Date.parse(lastChangeHistoryAt)
  if (Number.isNaN(timestampMs)) {
    return false
  }
  const windowMs = PACKAGING_ITEMS_LAST_UPDATED_FILTER_MS[parts.window]
  return timestampMs >= now.getTime() - windowMs
}
